import { AppNode, EdgeData, GroupNode, NodeType } from "@features/builder/graph/types"
import { nodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import { Edge, ReactFlowInstance, Rect, useReactFlow } from "@xyflow/react"
import { XYPosition, nodeToRect } from "@xyflow/system"

/**
 * Gets nodes intersecting with group at cursor position
 * @param nodes - All nodes in flow
 * @param position - reactflow cursor  position
 * @param reactFlowInstance - instance of reactflow, we need some functions from it
 *
 * @returns Intersecting nodes and group
 */
export function getNodesIntersectingWithGroup(
  nodes: AppNode[],
  position: XYPosition,
  reactFlowInstance: ReactFlowInstance<AppNode, Edge<EdgeData>>,
) {
  const group = getIntersectingGroup(position, reactFlowInstance)
  const nodesIntersectingWithGroup: AppNode[] = []

  for (const node of nodes) {
    const internalNode = reactFlowInstance.getInternalNode(node.id)
    if (!internalNode) continue
    if (internalNode.hidden) continue
    if (node.hidden) continue
    if (!group) continue

    // We need to use absolute position of node to determine intersection correctly - otherwise position
    // of node that is being dragged from one group to another will be wrong (positioned relatively to parent)
    const updatedNode = structuredClone(node)
    updatedNode.position = internalNode.internals.positionAbsolute
    updatedNode.parentId = undefined

    if (!reactFlowInstance.isNodeIntersecting(group, nodeToRect(updatedNode), true)) continue

    // do not add group itself as intersecting node
    if (node.id === group.id) continue

    nodesIntersectingWithGroup.push(node)
  }

  return {
    nodes: nodesIntersectingWithGroup,
    group: nodesIntersectingWithGroup.length > 0 ? group : undefined,
  }
}

/**
 * Gets nodes intersecting with group at cursor position
 * @param position - reactflow cursor  position
 * @param reactFlowInstance - instance of reactflow, we need some functions from it
 *
 * @returns Intersecting nodes and group
 */
export const getIntersectingGroup = (
  position: XYPosition,
  reactFlowInstance: ReactFlowInstance<AppNode, Edge<EdgeData>>,
) => {
  let group: GroupNode | undefined

  const point: Rect = { ...position, height: 1, width: 1 }

  for (const node of reactFlowInstance.getIntersectingNodes(point, true)) {
    if (node.dragging) continue
    if (node.hidden) continue
    if (node.type !== NodeType.Group) continue

    const isMostNested = !group || node.data.nestingLevel > group.data.nestingLevel
    if (isMostNested) group = node
  }

  return group
}

/**
 * Detects all intersections between nodes and groups
 *
 * This function analyzes three types of intersections:
 * 1. Groups intersecting with nodes (highlights group and node)
 * 2. Groups intersecting with other groups (highlights only if not in parent-child relationship)
 * 3. Nodes intersecting with other nodes (highlights both nodes)
 *
 * @param nodes - Array of all nodes in the flow diagram
 * @param getIntersectingNodes - ReactFlow hook function that detects geometric intersections
 *
 * @returns Object containing sets of IDs for:
 *   - intersectingGroup: Groups that intersect with other elements
 *   - intersectingNode: Nodes that intersect with other elements
 *   - childNodes: Child nodes of intersecting groups (for opacity styling)
 */
export function detectIntersections(
  nodes: AppNode[],
  getIntersectingNodes: ReturnType<
    typeof useReactFlow<AppNode, Edge<EdgeData>>
  >["getIntersectingNodes"],
) {
  const mapper = nodesGroupMapper(nodes)
  const intersectingGroup = new Set<string>()
  const intersectingNode = new Set<string>()
  const childNodes = new Set<string>()

  //Handle groups
  for (const [nodeId, node] of mapper.groupsMap) {
    if (node.hidden) continue

    const freeNodes = new Map(mapper.nodesMap)
    const freeGroups = new Map(mapper.groupsMap)

    // remove children's from current group
    mapper.groupToNodesIdsMapDeep.get(nodeId)?.forEach((nodeId) => freeNodes.delete(nodeId))
    mapper.parentToChildGroupsIdsMapDeep
      .get(nodeId)
      ?.forEach((groupId) => freeGroups.delete(groupId))

    // standard intersection
    const intersectingNodesAndGroups = getIntersectingNodes(node)

    for (const intersecting of intersectingNodesAndGroups) {
      if (intersecting.hidden) continue

      // Handle groups intersecting with nodes
      if (freeNodes.has(intersecting.id)) {
        intersectingGroup.add(nodeId)
        intersectingNode.add(intersecting.id)
        for (const childNode of mapper.groupToNodesIdsMapDeep.get(nodeId) ?? []) {
          // all children of intersecting parent will get a bit of opacity
          childNodes.add(childNode)
        }
      }

      // Handle group intersection with another group
      if (
        freeGroups.has(intersecting.id) &&
        // do not highlight parents intersecting with parents
        !mapper.childToParentGroupsIdsMapDeep.get(nodeId)?.includes(intersecting.id)
      ) {
        intersectingGroup.add(nodeId)
      }
    }
  }

  //Handle nodes
  for (const [nodeId, node] of mapper.nodesMap) {
    if (node.hidden) continue

    const intersectingNodes = getIntersectingNodes(node)

    for (const intersecting of intersectingNodes) {
      if (intersecting.id === nodeId) continue
      if (intersecting.type === NodeType.Group) continue
      if (intersecting.hidden) continue

      intersectingNode.add(intersecting.id)
      intersectingNode.add(nodeId)
    }
  }

  return { intersectingGroup, intersectingNode, childNodes }
}
